import { useTranslation } from 'react-i18next';
import { Fragment, useMemo } from 'react';
import { ArrowLeft, Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DIRECT_SALE_STEPS,
  isStepReachable,
  useDirectSaleStore,
  type DirectSaleStep,
} from '@/stores/directSaleStore';
import { readEmbedOptions } from './directSaleEmbed';

/**
 * Direct Sale's own header. Deliberately separate from the Seller Creator's
 * Header: the step sequence, the actor and the ending all differ, and folding
 * both into one component would add a third branching axis to a shell that
 * already carries two.
 */
export function DirectSaleHeader() {
  const { t } = useTranslation();
  const currentStep = useDirectSaleStore((s) => s.currentStep);
  const productId = useDirectSaleStore((s) => s.productId);
  const decoratedPositions = useDirectSaleStore((s) => s.decoratedPositions);
  const goToStep = useDirectSaleStore((s) => s.goToStep);
  const goToPreviousStep = useDirectSaleStore((s) => s.goToPreviousStep);

  const currentIndex = DIRECT_SALE_STEPS.indexOf(currentStep);
  // Fixed for the page's lifetime: the shop sets them on the iframe URL.
  const { header, shopName } = useMemo(() => readEmbedOptions(), []);

  const labelFor = (step: DirectSaleStep, index: number) =>
    `${index + 1}. ${t(`directSale.steps.${step}`)}`;

  const backLink = currentIndex > 0 && (
    <button
      type="button"
      onClick={goToPreviousStep}
      className="flex items-center gap-1.5 text-xs font-medium text-mute transition-colors hover:text-primary"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('directSale.nav.back')}
    </button>
  );

  // The shop embeds without a header: no steps, no name. The back link
  // stays, because it is the only way back to the product choice.
  if (!header && !backLink) return null;

  return (
    // The design step gives the whole screen to the editor, on the phone
    // and on the desktop: the embedding shop brings its own header, and the
    // editor leads to the products and to the cart itself. Hidden rather
    // than unmounted, so the step navigation stays in one place.
    <header
      className={cn(
        'sticky top-0 z-20 bg-paper',
        header ? 'border-b border-line' : 'px-4 py-1.5',
        currentStep === 'design' && 'hidden'
      )}
    >
      {header ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-1.5">
          {shopName && (
            <bdi
              className="max-w-[40%] truncate text-base font-bold text-ink"
              data-testid="direct-sale-shop-name"
            >
              {shopName}
            </bdi>
          )}

          <div className="flex-1" />

          <nav
            aria-label={t('directSale.steps.product')}
            className="flex flex-wrap justify-center gap-1.5 text-xs"
          >
            {DIRECT_SALE_STEPS.map((step, index) => {
              const isCurrent = step === currentStep;
              const isDone = index < currentIndex;
              const reachable = isStepReachable(step, {
                productId,
                decoratedPositions,
              });

              return (
                <Fragment key={step}>
                  {index > 0 && (
                    <ChevronRight
                      className="h-3.5 w-3.5 flex-none self-center text-mute"
                      aria-hidden="true"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => goToStep(step)}
                    disabled={!reachable || isCurrent}
                    aria-current={isCurrent ? 'step' : undefined}
                    className={cn(
                      'flex items-center gap-1.5 rounded-control px-3 py-1.5 font-medium transition-colors',
                      isCurrent && 'bg-primary font-semibold text-primary-fg',
                      !isCurrent &&
                        reachable &&
                        'border border-line bg-paper text-ink-2 hover:border-primary hover:text-primary',
                      !isCurrent &&
                        !reachable &&
                        'cursor-not-allowed border border-line bg-paper text-ink-3'
                    )}
                  >
                    {isDone && <Check className="h-3.5 w-3.5 text-success" />}
                    {labelFor(step, index)}
                  </button>
                </Fragment>
              );
            })}
          </nav>

          <div className="flex-1" />

          {backLink}
        </div>
      ) : (
        backLink
      )}
    </header>
  );
}
